import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import { DefaultValues, FormProvider, useFieldArray, useForm } from 'react-hook-form';

import Paper from 'components/paper';
import { Plus } from 'lucide-react';

import { useQueryClient } from '@tanstack/react-query';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { Prompt } from 'react-router-dom';
import _ from 'underscore';
import { copyName, nextSequentialName } from 'utils/name-utils';
import { getFeeds } from 'views/bluviu-nutrition/api/get-feeds';
import { putScenarioFeedProgram } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-put';
import FeedProgramItem from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/feed-program/feed-program-item';
import StepFooter from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/step-footer';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { FeedProgramForm } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-feed-program-form.type';
import { CurrencyPerUnit } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-financial-data.type';
import { ScenarioSetupStepProps } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-setup-step-props.type';
import { UpdateScenarioFeedProgramInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-feed-program-input.type';
import { mapFeedProgramsToForm } from 'views/bluviu-nutrition/features/scenarios-v2/utils/feed-program-utils';

type StepFeedProgramForm = {
  currencyPerUnit: CurrencyPerUnit;
  feedPrograms: FeedProgramForm[];
};

const StepFeedProgram: React.FC<ScenarioSetupStepProps> = ({ onClick, activeStep, isFirstStep, canProceed }) => {
  const params = useParams<R2ScenarioParams>();
  const queryClient = useQueryClient();
  const hasReset = useRef(false);
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const {
    account: { company },
  } = useAccountState();
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  const { data: feeds } = useGet(['getFeeds', company!.companyId], ({ queryKey }) => getFeeds(String(queryKey[1])), {
    enabled: !!company?.companyId,
    select: (data) => _.sortBy(data, (feed) => feed.name.toLowerCase()),
  });

  const defaultValues: DefaultValues<StepFeedProgramForm> = {
    currencyPerUnit: '' as CurrencyPerUnit,
    feedPrograms: [
      {
        name: 'Feed Program 1',
        rows: [{ feed: null, startFeedingAtFishSize: 0, price: 0 }],
      },
    ],
  };

  const form = useForm<StepFeedProgramForm>({
    defaultValues,
  });

  const { fields, append, remove, insert } = useFieldArray({
    control: form.control,
    name: 'feedPrograms',
  });

  const {
    formState: { isDirty },
  } = form;
  const unsavedChangesMessage = intl.formatMessage({ id: 'general.unsavedChangesPrompt' });

  useEffect(() => {
    if (hasReset.current || !feeds?.length || !scenario?.feedProgram?.length) return;

    const feedPrograms = mapFeedProgramsToForm(scenario.feedProgram, feeds);
    form.reset({ feedPrograms, currencyPerUnit: scenario.financialData?.currencyPerUnit || CurrencyPerUnit.NOK_PER_KG });
    hasReset.current = true;
  }, [feeds, scenario, form]);

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = unsavedChangesMessage;
      return unsavedChangesMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, unsavedChangesMessage]);

  const handleAddFeedProgram = useCallback(() => {
    const existingNames = form.getValues('feedPrograms').map((p) => p.name);
    append({
      name: nextSequentialName('Feed Program', existingNames),
      rows: [{ feed: null, startFeedingAtFishSize: 0, price: 0 }],
    });
  }, [append, form]);

  const updateScenarioFeedProgram = usePost<any, UpdateScenarioFeedProgramInput>(
    (data) => putScenarioFeedProgram(params.scenarioId, params.globalSiteId, data),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      queryClient.invalidateQueries({
        queryKey: ['getR2Scenario', params.globalSiteId, params.scenarioId],
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const handleCloneFeedProgram = useCallback(
    (index: number, currentProgramValues: FeedProgramForm) => {
      const existingNames = form.getValues('feedPrograms').map((p) => p.name);
      insert(index + 1, {
        name: copyName(currentProgramValues.name, existingNames),
        rows: currentProgramValues.rows || [{ feed: null, startFeedingAtFishSize: 0, price: 0 }],
      });
    },
    [insert, form]
  );

  const handleDeleteFeedProgram = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const handleSubmit = (form: StepFeedProgramForm, isSave: boolean) => {
    if (!isDirty && !!scenario?.feedProgram?.length && !isSave) {
      onClick(activeStep + 1);
      return;
    }

    const data: UpdateScenarioFeedProgramInput = {
      currencyPerUnit: form.currencyPerUnit,
      feedPrograms: form.feedPrograms.map((program) => ({
        name: program.name,
        feeds: program.rows
          .filter((row) => row.feed !== null)
          .map((row) => ({
            id: row.feed!.id,
            name: row.feed!.name,
            source: row.feed!.feedSource,
            startFeedingAtFishSize: row.startFeedingAtFishSize,
            price: row.price,
          })),
      })),
    };

    updateScenarioFeedProgram.mutate(data, isSave ? undefined : { onSuccess: () => onClick(activeStep + 1) });
  };

  const handleNext = form.handleSubmit((data) => handleSubmit(data, false));
  const handleSave = form.handleSubmit((data) => handleSubmit(data, true));

  return (
    <Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4, height: '100%' }}>
      <FormProvider {...form}>
        <Prompt when={isDirty} message={unsavedChangesMessage} />
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'flex-start'}>
          <Typography>Feed programs</Typography>
          <Box display={'flex'} flexDirection={'row'} alignItems={'flex-start'} gap={4}>
            <Select
              dark
              label="Currency per unit"
              controller={{
                control: form.control,
                name: 'currencyPerUnit',
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                },
              }}>
              <RadioSelectItem value={CurrencyPerUnit.NOK_PER_KG}>NOK/kg</RadioSelectItem>
              <RadioSelectItem value={CurrencyPerUnit.USD_PER_TON}>USD/1t</RadioSelectItem>
            </Select>
            <Button variant="outlined" onClick={handleAddFeedProgram} startIcon={<Plus size={20} />}>
              Add program
            </Button>
          </Box>
        </Box>
        <Divider />
        <Box flex={'1 1 auto'} display={'flex'} flexDirection={'column'} gap={2}>
          {fields.map((field, index) => (
            <FeedProgramItem
              key={field.id}
              control={form.control}
              index={index}
              feeds={feeds || []}
              onClone={handleCloneFeedProgram}
              onDelete={() => handleDeleteFeedProgram(index)}
              canDelete={fields.length > 1}
            />
          ))}
        </Box>
        <Divider />
        <StepFooter
          onPrev={() => onClick(activeStep - 1)}
          onNext={handleNext}
          onSkip={() => onClick(activeStep + 1)}
          onSave={handleSave}
          disablePrev={updateScenarioFeedProgram.isPending}
          disableNext={!canProceed || updateScenarioFeedProgram.isPending}
          nextIcon={updateScenarioFeedProgram.isPending ? <CircularProgress size={20} /> : undefined}
        />
      </FormProvider>
    </Paper>
  );
};

export default StepFeedProgram;
